/**
 * The authenticated application shell + route gate.
 *
 * `<RequireSession>` enforces a live session — bouncing to the external login
 * site on a miss and showing the loop-guard fallback if a redirect loop trips.
 * Inside it, {@link SessionScopedProviders} supplies flags/tenant/permissions
 * from the session claims, then the sidebar + header frame the routed `Outlet`.
 */
import { AlertTriangle } from "lucide-react";
import { Outlet } from "@tanstack/react-router";
import { RequireSession } from "@authe/ui-core/session";
import { AskAiProvider } from "./ask-ai-shell";
import { SessionScopedProviders } from "./providers";
import { Sidebar } from "@/components/shell/Sidebar";
import { Header } from "@/components/shell/Header";
import { APP_NAME, DASHBOARD_ERRORS } from "@/config";

function SigningIn(): React.ReactElement {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-background text-foreground">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
      <p className="text-sm text-muted-foreground">Signing in to {APP_NAME}…</p>
    </div>
  );
}

function AuthBlocked(): React.ReactElement {
  return (
    <div className="flex h-screen items-center justify-center bg-background p-6">
      <div className="flex max-w-sm flex-col items-center text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <p className="text-sm font-semibold text-foreground">{DASHBOARD_ERRORS.SESSION_LOOP.title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{DASHBOARD_ERRORS.SESSION_LOOP.description}</p>
      </div>
    </div>
  );
}

export function RootLayout(): React.ReactElement {
  return (
    <RequireSession loading={<SigningIn />} blocked={<AuthBlocked />}>
      <SessionScopedProviders>
        <AskAiProvider>
          <div className="flex h-screen overflow-hidden bg-background">
            <Sidebar />
            <div className="flex min-w-0 flex-1 flex-col">
              <Header />
              <main className="flex-1 overflow-y-auto p-6">
                <Outlet />
              </main>
            </div>
          </div>
        </AskAiProvider>
      </SessionScopedProviders>
    </RequireSession>
  );
}
